import { useEffect, useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useWorkspaceContext } from '@/contexts/WorkspaceContext';
import { useAuth } from './useAuth';
import { toast } from 'sonner';
import { logger } from '@/lib/logger';

export function useUnreadTickets() {
  const [count, setCount] = useState(0);
  const { activeWorkspace } = useWorkspaceContext();
  const { user } = useAuth();

  const fetchCount = useCallback(async () => {
    if (!activeWorkspace?.id || !user?.id) {
      setCount(0);
      return;
    }

    try {
      // Use the optimized RPC function (single query instead of N+1)
      const { data, error } = await supabase.rpc('get_unread_tickets_count', {
        p_workspace_id: activeWorkspace.id,
        p_user_id: user.id
      });

      if (error) {
        logger.error('Failed to fetch unread tickets count', error, 'useUnreadTickets');
        toast.error('Erreur lors du calcul des tickets non lus');
        return;
      }

      setCount(data || 0);
    } catch (error) {
      logger.error('Failed to fetch unread tickets count', error, 'useUnreadTickets');
      toast.error('Erreur lors du calcul des tickets non lus');
    }
  }, [activeWorkspace?.id, user?.id]);

  useEffect(() => {
    fetchCount();

    if (!activeWorkspace?.id || !user?.id) return;

    // Listen for realtime changes on tickets, messages, and reads
    // Use a unique channel name per instance to avoid "cannot add callbacks after subscribe" errors
    const channelName = `unread-tickets-count-${activeWorkspace.id}-${user.id}-${Math.random().toString(36).slice(2, 10)}`;
    const ticketsChannel = supabase
      .channel(channelName)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'tickets',
          filter: `workspace_id=eq.${activeWorkspace.id}`,
        },
        () => fetchCount()
      )
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'ticket_messages',
        },
        () => fetchCount()
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'ticket_reads',
          filter: `user_id=eq.${user.id}`,
        },
        () => fetchCount()
      )
      .subscribe();

    return () => {
      supabase.removeChannel(ticketsChannel);
    };
  }, [activeWorkspace?.id, user?.id, fetchCount]);

  return count;
}
